import { InfoTip } from "@/components/ui/info-tip";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Permission, useCan, useZone } from "@/components/session-provider";
import { formatDate } from "@/lib/time";
import {
  DEFLECTION_REPORT_DAYS,
  getDeflectionReport,
  type DeflectionReport,
} from "@/lib/reports-deflection";
import { getReference } from "@/lib/tickets";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/reports/deflection")({
  component: DeflectionReportPage,
});

/**
 * **Self-service deflection.**
 *
 * ⚠️ **"SELF-SERVICE", NEVER "AI" — see `ReportDeflection`'s own remarks.** Two of the three
 * sources on this page are deterministic rules an administrator wrote, not a model. Only the
 * chat assistant is a model, and even it cannot act outside a closed reply schema. Calling
 * this "AI success" would claim something the product does not do.
 *
 * ⚠️ **Gated on `report.read` alone**, not the ticket reporting tiers — the figures here are
 * organisation-wide by construction, so the narrower tier that reaches everything is the only
 * one that means anything.
 *
 * ⚠️ **The assistant's display name is fetched, never hardcoded** — see
 * `DeflectionSummaryCard`'s own remarks for why "TARS" stays the internal name throughout the
 * code while the screen shows whatever an Owner or Super Admin set in Settings → Assistance.
 */
function DeflectionReportPage() {
  const allowed = useCan(Permission.ReportRead);
  const zone = useZone();

  const [days, setDays] = useState<number>(14);
  const [report, setReport] = useState<DeflectionReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [assistantName, setAssistantName] = useState("the chat assistant");

  useEffect(() => {
    if (!allowed) return;

    let current = true;
    setLoading(true);
    setError(null);

    getDeflectionReport(days)
      .then((r) => {
        if (current) setReport(r);
      })
      .catch((e) => {
        // A failure renders as a failure, never as an empty report (D-122).
        if (current) {
          setReport(null);
          setError(e instanceof ApiError ? e.message : "That report could not be loaded.");
        }
      })
      .finally(() => {
        if (current) setLoading(false);
      });

    return () => {
      current = false;
    };
  }, [allowed, days]);

  useEffect(() => {
    if (!allowed) return;

    let current = true;

    getReference()
      .then((r) => {
        if (current && r.assistantName) setAssistantName(r.assistantName);
      })
      .catch(() => {
        // Cosmetic only — the fallback label still reads sensibly.
      });

    return () => {
      current = false;
    };
  }, [allowed]);

  const guidanceRows = useMemo(() => report?.guidance.filter((g) => g.shown > 0) ?? [], [report]);

  if (!allowed) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6">
        <p className="text-sm text-muted-foreground">This report is not available to you.</p>
      </div>
    );
  }

  const resolvedRate =
    report && report.tars.sessions > 0
      ? Math.round((report.tars.resolved / report.tars.sessions) * 100)
      : null;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <div>
        <Link
          to="/reports"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Reports
        </Link>
        <h1 className="mt-2 text-xl font-semibold">Self-service deflection</h1>
        <p className="text-sm text-muted-foreground">
          How often {assistantName}, the knowledge base and intake guidance kept a request off the
          desk — and which topics they are failing to resolve, so that can be fixed proactively
          rather than deflected blindly.
        </p>
      </div>

      <div className="flex gap-2">
        {DEFLECTION_REPORT_DAYS.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            className={cn(
              "rounded-md border px-3 py-1 text-sm",
              d === days ? "border-primary bg-primary/10" : "border-border",
            )}
          >
            {d} days
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading && !report && (
        <div className="h-32 animate-pulse rounded-2xl border border-border bg-card" />
      )}

      {report && (
        <>
          <section className="group/card rounded-lg border p-4">
            <h2 className="flex items-center gap-1 font-medium">
              {assistantName}
              <InfoTip id="deflect-assistant" />
            </h2>
            <p className="text-xs text-muted-foreground">
              Kept for {report.coverage.tarsRetentionDays} days before being removed — see{" "}
              <Link to="/settings" search={{ section: "ai-transcripts" }} className="underline">
                assistant transcripts
              </Link>
              . Earliest session still kept:{" "}
              {report.coverage.earliestRetainedTarsSessionUtc
                ? formatDate(report.coverage.earliestRetainedTarsSessionUtc, zone)
                : "none"}
              .
            </p>

            <div className="mt-3 grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-semibold">{report.tars.sessions}</p>
                <p className="text-xs text-muted-foreground">Conversations</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-emerald-600">{report.tars.resolved}</p>
                <p className="text-xs text-muted-foreground">
                  Resolved {resolvedRate !== null && `(${resolvedRate}%)`}
                </p>
              </div>
              <div>
                <p className="text-2xl font-semibold">{report.tars.handedOff}</p>
                <p className="text-xs text-muted-foreground">Handed off / in progress</p>
              </div>
            </div>
          </section>

          <section className="group/card rounded-lg border p-4">
            <h2 className="flex items-center gap-1 font-medium">
              Intake guidance
              <InfoTip id="deflect-guidance" />
            </h2>
            <p className="text-xs text-muted-foreground">
              Lifetime totals per rule, not windowed — these counters carry no timestamp.
            </p>

            {guidanceRows.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">No guidance rule has fired yet.</p>
            ) : (
              <table className="mt-2 w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="p-1">Rule</th>
                    <th className="p-1">Shown</th>
                    <th className="p-1">Answered</th>
                    <th className="p-1">Deflection rate</th>
                  </tr>
                </thead>
                <tbody>
                  {guidanceRows.map((g) => (
                    <tr key={g.ruleName} className="border-t">
                      <td className="p-1">
                        {g.ruleName}
                        {!g.isEnabled && (
                          <span className="ml-1 text-xs text-muted-foreground">(disabled)</span>
                        )}
                      </td>
                      <td className="p-1">{g.shown}</td>
                      <td className="p-1">{g.answered}</td>
                      <td className="p-1">{Math.round((g.answered / g.shown) * 100)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="group/card rounded-lg border p-4">
            <h2 className="flex items-center gap-1 font-medium">
              Knowledge base
              <InfoTip id="deflect-kb" />
            </h2>
            <p className="text-xs text-muted-foreground">
              Most-viewed articles in the window. A high view count with a low helpful share is the
              content gap worth writing a better article for.
            </p>

            {report.knowledgeBase.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">
                No article activity in this window.
              </p>
            ) : (
              <table className="mt-2 w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="p-1">Article</th>
                    <th className="p-1">Views</th>
                    <th className="p-1">Said helpful</th>
                    <th className="p-1">Said not helpful</th>
                  </tr>
                </thead>
                <tbody>
                  {report.knowledgeBase.map((a) => (
                    <tr key={a.title} className="border-t">
                      <td className="p-1">{a.title}</td>
                      <td className="p-1">{a.views}</td>
                      <td className="p-1 text-emerald-600">{a.helpfulAnswers}</td>
                      <td className="p-1">{a.unhelpfulAnswers}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
